import { useMemo } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { it } from 'date-fns/locale';
import { useApp } from '@/lib/context';
import { useOnlineUsers } from '@/hooks/use-presence';
import { useIsDesktop, useIsXLDesktop } from '@/hooks/use-device-context';
import TelegramImage from '@/components/profile/telegram-image';
import { KG } from './desktop-tokens';
import { useDetailPanel } from './detail-panel-context';

interface ChatConversationSummary {
  interaction: {
    id: number;
    user1Id: number;
    user2Id: number;
    lastInteractionAt: string;
  };
  otherUser: {
    id: number;
    username: string;
    category: string;
    photos?: any[];
    chatDisplayName?: string | null;
    partnerChatDisplayName?: string | null;
  };
  lastMessage?: {
    id: number;
    senderId: number;
    receiverId: number;
    content: string;
    createdAt: string;
  };
  unreadCount: number;
}

interface DesktopChatRailProps {
  width?: number;
}

export function getChatRailWidth(isXL: boolean, isDesktop: boolean): number {
  if (isXL) return 380;
  if (isDesktop) return 300;
  return 260;
}

export function DesktopChatRail({ width }: DesktopChatRailProps) {
  const { user } = useApp();
  const [location, setLocation] = useLocation();
  const { open: openDetailPanel, current: activePanel } = useDetailPanel();
  const isDesktop = useIsDesktop();
  const isXL = useIsXLDesktop();
  const effectiveWidth = width ?? getChatRailWidth(isXL, isDesktop);
  const { data: onlineUsersData } = useOnlineUsers();
  const onlineUserIds = useMemo(
    () =>
      new Set(
        onlineUsersData?.onlineUsers
          ?.filter((u: any) => u.isOnline)
          .map((u: any) => u.userId) || []
      ),
    [onlineUsersData]
  );

  const { data: conversations = [] } = useQuery<ChatConversationSummary[]>({
    queryKey: ['/api/chat/conversations'],
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchInterval: 15000,
  });

  const items = conversations
    .filter((c) => c.lastMessage !== undefined)
    .sort(
      (a, b) =>
        new Date(b.interaction.lastInteractionAt).getTime() -
        new Date(a.interaction.lastInteractionAt).getTime()
    )
    .slice(0, 12);

  const totalUnread = conversations.reduce((acc, c) => acc + (c.unreadCount || 0), 0);

  const openConversation = (conv: ChatConversationSummary) => {
    // Desktop: open the conversation inside the right-anchored DetailPanel.
    // Keep dispatching the legacy event so anyone still listening (e.g. mobile
    // /chat page) stays in sync if visible.
    const event = new CustomEvent('openChat', {
      detail: {
        userId: conv.otherUser.id,
        interactionId: conv.interaction.id,
      },
    });
    window.dispatchEvent(event);
    openDetailPanel({ kind: 'chat', id: conv.otherUser.id });
  };

  const displayName = (conv: ChatConversationSummary) => {
    const u = conv.otherUser;
    if ((user as any)?.isPartner && u.partnerChatDisplayName) return u.partnerChatDisplayName;
    if (u.chatDisplayName) return u.chatDisplayName;
    return u.username;
  };

  return (
    <aside
      style={{
        width: effectiveWidth,
        flexShrink: 0,
        background: KG.bg,
        borderLeft: `1px solid ${KG.border}`,
        display: 'flex',
        flexDirection: 'column',
        // Sits below the 64px top bar, so fill the remaining viewport height.
        height: 'calc(100vh - 64px)',
        position: 'sticky',
        top: 64,
      }}
    >
      <div
        style={{
          height: 64,
          padding: '0 18px',
          borderBottom: `1px solid ${KG.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 18 }}>💬</span>
        <div style={{ fontSize: 14, fontWeight: 700, color: KG.text }}>Messaggi</div>
        {totalUnread > 0 && (
          <span
            style={{
              marginLeft: 6,
              fontSize: 11,
              padding: '2px 7px',
              borderRadius: 99,
              background: KG.blueSoft,
              color: '#84a8ff',
              fontWeight: 700,
            }}
          >
            {totalUnread > 99 ? '99+' : totalUnread} {totalUnread === 1 ? 'nuovo' : 'nuovi'}
          </span>
        )}
        <button
          type="button"
          onClick={() => setLocation('/chat')}
          aria-label="Apri chat"
          style={{
            marginLeft: 'auto',
            width: 30,
            height: 30,
            borderRadius: 8,
            background: KG.panel,
            border: `1px solid ${KG.border}`,
            color: KG.text2,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Plus size={14} strokeWidth={1.8} />
        </button>
      </div>

      <div style={{ padding: 8, overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {items.length === 0 && (
          <div
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              color: KG.hint,
              fontSize: 12,
            }}
          >
            Nessuna conversazione ancora.
          </div>
        )}

        {items.map((conv) => {
          const name = displayName(conv);
          const isOnline = onlineUserIds.has(conv.otherUser.id);
          const time = conv.lastMessage
            ? formatDistanceToNowStrict(new Date(conv.lastMessage.createdAt), {
                addSuffix: false,
                locale: it,
              })
                .replace('minuti', 'min')
                .replace('minuto', 'min')
                .replace('secondi', 's')
                .replace('secondo', 's')
                .replace(' ore', 'h')
                .replace(' ora', 'h')
                .replace(' giorni', 'g')
                .replace(' giorno', 'g')
            : '';
          const preview = conv.lastMessage?.content || '';
          const sentByMe = conv.lastMessage?.senderId === (user as any)?.id;
          const previewText = sentByMe ? `Tu: ${preview}` : preview;
          const isActive =
            activePanel?.kind === 'chat' && Number(activePanel.id) === conv.otherUser.id;

          return (
            <button
              key={conv.interaction.id}
              type="button"
              onClick={() => openConversation(conv)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: 10,
                borderRadius: 12,
                background: isActive ? KG.cardHi : 'transparent',
                width: '100%',
                border: 'none',
                color: KG.text,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textAlign: 'left',
                transition: 'background 120ms',
              }}
              onMouseEnter={(e) => {
                if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = KG.panel;
              }}
              onMouseLeave={(e) => {
                if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <TelegramImage
                  photos={conv.otherUser.photos as any}
                  userId={conv.otherUser.id}
                  username={conv.otherUser.username}
                  alt={conv.otherUser.username}
                  className="rounded-full"
                  variant="avatar"
                  style={{ width: 38, height: 38, borderRadius: 999 }}
                />
                {isOnline && (
                  <span
                    style={{
                      position: 'absolute',
                      right: -1,
                      bottom: -1,
                      width: 10,
                      height: 10,
                      borderRadius: 99,
                      background: KG.online,
                      boxShadow: `0 0 0 2px ${KG.bg}`,
                    }}
                  />
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {name}
                  </span>
                  {time && (
                    <span style={{ fontSize: 10, color: KG.hint2, flexShrink: 0 }}>{time}</span>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 3,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: conv.unreadCount > 0 ? KG.text2 : KG.hint,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {previewText || '—'}
                  </span>
                  {conv.unreadCount > 0 && (
                    <span
                      style={{
                        minWidth: 18,
                        height: 18,
                        padding: '0 5px',
                        background: KG.blue,
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 700,
                        borderRadius: 99,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div
        style={{
          padding: 12,
          borderTop: `1px solid ${KG.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: KG.hint,
          fontSize: 12,
          flexShrink: 0,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 99, background: KG.online }} />
        <span>Online · Connesso via socket</span>
      </div>
    </aside>
  );
}

export default DesktopChatRail;
